// Enterprise pricing, in one place.
//
// These four numbers were duplicated verbatim in App.tsx and
// EnterpriseUpgradeModal.tsx, and the annual-discount copy had already drifted
// apart across three surfaces (~17% in the signup form, 20% everywhere else).
//
// **These are DISPLAY figures.** What a customer is actually charged comes from
// the Stripe price objects behind STRIPE_PRICE_ENTERPRISE_{BASE,SEAT}_{MONTHLY,
// ANNUAL}; nothing cross-checks the two. Changing a price in Stripe means
// changing it here too, or the quoted total silently stops matching the charge.

export const ENTERPRISE_PRICING = {
  monthlyBase: 80,
  monthlySeat: 25,
  annualBase: 768,
  annualSeat: 240,
} as const;

/** Seats a firm may buy. One, because account admin is free and seats are paid. */
export const SEAT_MIN = 1;
export const SEAT_MAX = 25;

export type BillingCycle = 'monthly' | 'annual';

/** What the firm pays per billing period for `seats` seats. */
export function enterpriseTotal(seats: number, cycle: BillingCycle): number {
  const { monthlyBase, monthlySeat, annualBase, annualSeat } = ENTERPRISE_PRICING;
  return cycle === 'monthly'
    ? monthlyBase + monthlySeat * seats
    : annualBase + annualSeat * seats;
}

/** What a year costs on each cycle, for comparing them honestly. */
export function annualSavings(seats: number): number {
  return enterpriseTotal(seats, 'monthly') * 12 - enterpriseTotal(seats, 'annual');
}

/**
 * The annual discount, as a whole-number percent.
 *
 * It is exactly 20% at every seat count — base and per-seat are each discounted
 * 20% (960→768, 300→240), so the ratio is seat-independent. Computed rather
 * than written down so the copy cannot drift from the numbers again.
 */
export function annualDiscountPercent(seats: number = 1): number {
  const yearlyOnMonthly = enterpriseTotal(seats, 'monthly') * 12;
  return Math.round((annualSavings(seats) / yearlyOnMonthly) * 100);
}

/**
 * Clamp user input to the purchasable range.
 *
 * Only NaN gets special handling — an empty number input parses to NaN, and
 * comparisons against it are all false, so it would slip through the clamp.
 * Infinities are left to the clamp itself, which resolves them to the bound
 * they belong at rather than collapsing both to the minimum.
 */
export function clampSeats(value: number): number {
  if (Number.isNaN(value)) return SEAT_MIN;
  return Math.max(SEAT_MIN, Math.min(SEAT_MAX, Math.floor(value)));
}
